import { useState } from "react";
import { apiPost, ApiError } from "../shared/api.js";
import { copy, errorText } from "../shared/copy.js";
import { Button } from "./Button.js";
import { TextField } from "./Field.js";
import { track } from "../shared/analytics.js";
import styles from "./ReviewPrompt.module.css";

interface Props {
  sessionId: string;
  cityGisUrl: string | null;
  cityYandexUrl: string | null;
}

type Step =
  | "ask"
  | "positive"        // pick 2GIS / Yandex
  | "positiveDone"    // "Спасибо!"
  | "negative"        // free-form text
  | "negativeDone";   // "Спасибо, разберёмся"

interface ReviewResp {
  id: string;
  redirectUrl: string | null;
}

export function ReviewPrompt(props: Props) {
  const [step, setStep] = useState<Step>("ask");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(payload: {
    sentiment: "POSITIVE" | "NEGATIVE";
    platform?: "GIS_2GIS" | "YANDEX_MAPS" | "INTERNAL";
    text?: string;
  }) {
    setBusy(true);
    setError("");
    try {
      const body: Record<string, unknown> = {
        sessionId: props.sessionId,
        sentiment: payload.sentiment,
      };
      if (payload.platform) body["platform"] = payload.platform;
      if (payload.text) body["text"] = payload.text;
      const res = await apiPost<ReviewResp>("/api/review", body);
      return res;
    } catch (e) {
      setError(
        e instanceof ApiError ? errorText(e.code) : copy.common.somethingWrong,
      );
      return null;
    } finally {
      setBusy(false);
    }
  }

  if (step === "positiveDone") {
    return <p className={styles.thanks}>{copy.s6.positiveThanks}</p>;
  }
  if (step === "negativeDone") {
    return <p className={styles.thanks}>{copy.s6.negativeThanks}</p>;
  }

  if (step === "ask") {
    return (
      <div className={styles.wrap}>
        <p className={styles.prompt}>{copy.s6.prompt}</p>
        <div className={styles.row}>
          <Button
            block
            onClick={() => {
              track({
                name: "review_started",
                sessionId: props.sessionId,
                props: { sentiment: "positive" },
              });
              setStep("positive");
            }}
            disabled={busy}
          >
            {copy.s6.positive}
          </Button>
          <Button
            block
            variant="secondary"
            onClick={() => {
              track({
                name: "review_started",
                sessionId: props.sessionId,
                props: { sentiment: "negative" },
              });
              setStep("negative");
            }}
            disabled={busy}
          >
            {copy.s6.negative}
          </Button>
        </div>
      </div>
    );
  }

  if (step === "positive") {
    return (
      <div className={styles.wrap}>
        <p className={styles.prompt}>{copy.s6.positiveTitle}</p>
        <p className={styles.hint}>{copy.s6.positiveHint}</p>
        {props.cityGisUrl && (
          <Button
            block
            onClick={async () => {
              const r = await submit({
                sentiment: "POSITIVE",
                platform: "GIS_2GIS",
              });
              if (r?.redirectUrl) window.open(r.redirectUrl, "_blank");
              setStep("positiveDone");
            }}
            disabled={busy}
          >
            {copy.s6.gis}
          </Button>
        )}
        {props.cityYandexUrl && (
          <Button
            block
            variant="secondary"
            onClick={async () => {
              const r = await submit({
                sentiment: "POSITIVE",
                platform: "YANDEX_MAPS",
              });
              if (r?.redirectUrl) window.open(r.redirectUrl, "_blank");
              setStep("positiveDone");
            }}
            disabled={busy}
          >
            {copy.s6.yandex}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={() => setStep("positiveDone")}
          disabled={busy}
        >
          {copy.s6.skip}
        </Button>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }

  // step === "negative"
  return (
    <div className={styles.wrap}>
      <p className={styles.prompt}>{copy.s6.negativeTitle}</p>
      <p className={styles.hint}>{copy.s6.negativeHint}</p>
      <TextField
        label=""
        placeholder={copy.s6.negativePlaceholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={2000}
      />
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.row}>
        <Button
          variant="secondary"
          onClick={() => setStep("ask")}
          disabled={busy}
        >
          {copy.common.back}
        </Button>
        <Button
          block
          disabled={busy || text.trim().length === 0}
          onClick={async () => {
            const r = await submit({
              sentiment: "NEGATIVE",
              platform: "INTERNAL",
              text: text.trim(),
            });
            if (r) setStep("negativeDone");
          }}
        >
          {copy.s6.negativeSubmit}
        </Button>
      </div>
    </div>
  );
}
